/**
 * Check if code is running on the server (no window object)
 */
export function isServer(): boolean {
  return typeof window === 'undefined';
}

/**
 * Parse a storage value with optional custom deserializer.
 * If JSON parsing fails, returns the raw string value (for backwards compatibility
 * with values stored without JSON.stringify).
 */
export function parseStorageValue<T>(
  value: string | null,
  defaultValue: T,
  deserializer?: (value: string) => T
): T {
  if (value === null) return defaultValue;

  try {
    if (deserializer) {
      return deserializer(value);
    }
    return JSON.parse(value) as T;
  } catch {
    // If JSON parsing fails, return the raw string value for backwards compatibility
    // This handles cases where values were stored without JSON.stringify
    return value as unknown as T;
  }
}

/**
 * Get an item from localStorage with type safety
 */
export function getStorageItem<T>(
  key: string,
  defaultValue: T,
  deserializer?: (value: string) => T
): T {
  if (isServer()) return defaultValue;

  try {
    const value = localStorage.getItem(key);
    return parseStorageValue(value, defaultValue, deserializer);
  } catch (error) {
    console.error(`Error getting localStorage item "${key}":`, error);
    return defaultValue;
  }
}

/**
 * Set an item in localStorage with optional custom serializer
 */
export function setStorageItem(
  key: string,
  value: unknown,
  serializer?: (value: unknown) => string
): void {
  if (isServer()) return;

  try {
    const stringValue = serializer ? serializer(value) : JSON.stringify(value);
    localStorage.setItem(key, stringValue);
  } catch (error) {
    console.error(`Error setting localStorage item "${key}":`, error);
  }
}

/**
 * Remove an item from localStorage
 */
export function removeStorageItem(key: string): void {
  if (isServer()) return;

  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`Error removing localStorage item "${key}":`, error);
  }
}

/**
 * Clear all items from localStorage
 */
export function clearStorage(): void {
  if (isServer()) return;

  try {
    localStorage.clear();
  } catch (error) {
    console.error('Error clearing localStorage:', error);
  }
}
